/**
 * Team roles (docs/spec/08 §4; server is authoritative):
 * owner · admin (manage members / catalog / caps) · member (create & edit projects, use credits)
 * · viewer (read-only). Members cannot change shared prices or branding.
 */
export type Role = 'owner' | 'admin' | 'member' | 'viewer';

export const ROLE_LABELS: Record<Role, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' };
const RANK: Record<Role, number> = { viewer: 0, member: 1, admin: 2, owner: 3 };

export const isAdmin = (r: Role | null | undefined) => r === 'owner' || r === 'admin';

/** Roles `actor` may give to someone (ownership moves only via transfer). */
export function assignableRoles(actor: Role): Role[] {
  if (actor === 'owner') return ['admin', 'member', 'viewer'];
  if (actor === 'admin') return ['member', 'viewer'];
  return [];
}

/** Roles `actor` may invite with. */
export function invitableRoles(actor: Role): ('admin' | 'member' | 'viewer')[] {
  return assignableRoles(actor).filter((r): r is 'admin' | 'member' | 'viewer' => r !== 'owner');
}

/** Whether `actor` may change `target`'s role (never their own; admins only below admin). */
export function canChangeRole(actor: Role, target: Role, isSelf: boolean): boolean {
  if (isSelf || target === 'owner') return false;
  if (actor === 'owner') return true;
  return actor === 'admin' && RANK[target] < RANK.admin;
}

/** Whether `actor` may remove `target` (leaving = removing yourself; the owner must transfer first). */
export function canRemove(actor: Role, target: Role, isSelf: boolean): boolean {
  if (isSelf) return actor !== 'owner';
  if (target === 'owner') return false;
  if (actor === 'owner') return true;
  return actor === 'admin' && RANK[target] < RANK.admin;
}

export const canTransferOwnership = (actor: Role) => actor === 'owner';
export const canEditCatalog = (r: Role | null | undefined) => isAdmin(r);
